import { deleteDB, openDB, type IDBPDatabase } from "idb";

import { legacySecurityDbName } from "@/lib/legacy-storage";

/**
 * Fila de registos feitos sem rede (App do Segurança).
 *
 * Numa portaria a rede falha — e um registo de entrada ou uma ocorrência NÃO
 * se pode perder por causa disso. Quando o envio falha por falta de rede, o
 * pedido fica aqui guardado e é reenviado assim que a ligação volta.
 *
 * Porquê IndexedDB e não memória/localStorage:
 *  • memória morre ao fechar a app (o guarda fecha, o registo evapora-se);
 *  • localStorage é síncrono e só guarda strings — a foto de uma ocorrência é
 *    um File/Blob, que o IndexedDB guarda nativamente.
 */

export type QueuedKind = "gate-entry" | "incident" | "visit-check-in" | "visit-check-out";

/**
 * Monta o FormData de um payload guardado na fila.
 *
 * Existe aqui (e não em cada hook) porque o payload é reconstruído em DOIS
 * sítios — no envio imediato e no reenvio da fila — e duas cópias divergem.
 * Trata `File`/`Blob` soltos e arrays deles (os anexos múltiplos).
 */
export const buildForm = (payload: Record<string, unknown>): FormData => {
  const form = new FormData();
  for (const [key, value] of Object.entries(payload)) {
    if (value === null || value === undefined) continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item instanceof Blob) form.append(key, item);
      }
      continue;
    }
    if (value instanceof Blob) {
      form.append(key, value);
      continue;
    }
    form.append(key, String(value));
  }
  return form;
};

export interface QueuedItem {
  id: string;
  kind: QueuedKind;
  /** Corpo do pedido. Em `incident` pode trazer um File (a foto). */
  payload: Record<string, unknown>;
  /** Texto curto para o segurança perceber o que está pendente. */
  label: string;
  createdAt: number;
  /** Tentativas de envio já falhadas (só para diagnóstico). */
  attempts: number;
}

// O nome da base IndexedDB no browser de quem usa o posto de segurança.
//
// Até 2026-09-01 levava o nome de um cliente escrito no código. Foi trocado por este, neutro, mas renomear uma base
// NÃO migra o que lá está: a fila de picagens por enviar ficava órfã e perdiam-se registos de entradas e saídas
// reais. Por isso, ao abrir a base nova, `migrarBaseAntiga` copia o que houver na antiga (cujo nome deriva da marca da
// build, ver `lib/legacy-storage.ts`) e só depois a apaga.
const DB_NAME = "aihumanerp-security";
const STORE = "outbox";

/**
 * A base `nome` existe? Abre-a só para ver: se o browser quiser criá-la (`upgradeneeded`), é porque não existia, e a
 * criação é abortada para não deixar uma base vazia atrás. API nativa, e não `idb`: o `openDB` transforma esse abort
 * numa rejeição que ninguém apanha.
 */
const baseExiste = (nome: string): Promise<boolean> =>
  new Promise((resolve) => {
    let criada = false;
    const pedido = indexedDB.open(nome);
    pedido.onupgradeneeded = () => {
      criada = true;
      pedido.transaction?.abort();
    };
    pedido.onsuccess = () => {
      pedido.result.close();
      resolve(!criada);
    };
    pedido.onerror = (evento) => {
      evento.preventDefault();
      resolve(false);
    };
    pedido.onblocked = () => resolve(false);
  });

/**
 * Copia para a base nova os registos por enviar da base antiga e apaga-a. Idempotente (o `put` é por `id`) e nunca
 * deita a fila abaixo: se falhar, a base antiga fica como está e tenta-se outra vez na próxima abertura.
 */
const migrarBaseAntiga = async (destino: IDBPDatabase): Promise<void> => {
  const antigo = legacySecurityDbName(DB_NAME);
  if (!antigo) return;
  let origem: IDBPDatabase;
  try {
    if (!(await baseExiste(antigo))) return; // o caso normal: nunca houve base antiga
    origem = await openDB(antigo);
  } catch {
    return; // não se consegue abrir: fica como está
  }
  try {
    if (origem.objectStoreNames.contains(STORE)) {
      const itens: QueuedItem[] = await origem.getAll(STORE);
      if (itens.length > 0) {
        const tx = destino.transaction(STORE, "readwrite");
        await Promise.all([...itens.map((item) => tx.store.put(item)), tx.done]);
      }
    }
    origem.close();
    // Sem `await`: se outro separador ainda tiver a base aberta o apagar fica pendente, e não se bloqueia a fila.
    void deleteDB(antigo).catch(() => undefined);
  } catch {
    origem.close();
  }
};

let dbPromise: Promise<IDBPDatabase> | null = null;

const getDB = () => {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: "id" });
        }
      },
    }).then(async (db) => {
      await migrarBaseAntiga(db);
      return db;
    });
  }
  return dbPromise;
};

/** Guarda um registo por enviar. Devolve o item criado. */
export const enqueue = async (
  kind: QueuedKind,
  payload: Record<string, unknown>,
  label: string,
): Promise<QueuedItem> => {
  const item: QueuedItem = {
    id: crypto.randomUUID(),
    kind,
    payload,
    label,
    createdAt: Date.now(),
    attempts: 0,
  };
  const db = await getDB();
  await db.put(STORE, item);
  return item;
};

/** Todos os registos pendentes, do mais antigo para o mais recente. */
export const listQueued = async (): Promise<QueuedItem[]> => {
  const db = await getDB();
  const items: QueuedItem[] = await db.getAll(STORE);
  return items.sort((a, b) => a.createdAt - b.createdAt);
};

export const removeQueued = async (id: string): Promise<void> => {
  const db = await getDB();
  await db.delete(STORE, id);
};

export const markAttempt = async (item: QueuedItem): Promise<void> => {
  const db = await getDB();
  await db.put(STORE, { ...item, attempts: item.attempts + 1 });
};

export const countQueued = async (): Promise<number> => {
  const db = await getDB();
  return db.count(STORE);
};

/**
 * Distingue "não houve rede" de "o servidor recusou".
 *
 * É a decisão que importa: um erro de rede volta para a fila (tentamos outra
 * vez); um 4xx (ex.: dados inválidos, sessão expirada) NUNCA volta para a fila,
 * senão fica lá preso a tentar para sempre.
 *
 * O interceptor em `lib/api.ts` normaliza os erros e põe-lhes `status` a partir
 * de `error.response?.status` — logo, **sem `status` = não chegámos ao servidor**.
 * É esse o contrato em que nos apoiamos (e não na forma interna do axios).
 */
export const isOfflineError = (error: unknown): boolean => {
  const status = (error as { status?: number } | null)?.status;
  if (typeof status === "number") return false; // o servidor respondeu → não é falta de rede
  return true;
};
